/**
 * A timeline component that displays a log entry with user avatar, metadata,
 * and optional expandable content. Used to show a chronological history of
 * actions or changes, typically in a version history or audit log context.
 *
 * The component renders:
 * - A user avatar with initials
 * - A vertical timeline line connecting to other timeline items
 * - A header section with username, date/time, and action message
 * - Optional expandable content area (when isExpanded is true)
 *
 * @param props - The component props
 * @param props.id - Unique identifier for the timeline item (used for DOM id attribute)
 * @param props.username - The name of the user who performed the action (used for avatar initials)
 * @param props.date - The date string to display (will be formatted with time)
 * @param props.message - The log message content, typically describing what action was taken
 * @param props.isExpanded - Whether to show the expandable content area. Defaults to true.
 * @param props.children - Optional React nodes to display in the expandable content area
 *
 * @example
 * ```tsx
 * <LogTimeline
 *   id="log-123"
 *   username="John Doe"
 *   date="2024-01-15T10:30:00Z"
 *   message={<span>Modified <b>Product Name</b></span>}
 *   isExpanded={true}
 * >
 *   <div>Additional details about the change...</div>
 * </LogTimeline>
 * ```
 */

import styled from "styled-components";
import { LogHeader } from "./LogHeader";
import { formatDateTimeWithTime } from "../../util/util";
import { getInitials } from "../Avatar/Avatar";

const ItemContainer = styled.div`
  position: relative;
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  align-items: stretch;
`;

const AvatarColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 24px;
`;

const TimelineLineContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const AvatarWrapper = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_standard")};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const Avatar = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.resolve("color_bg_surface_brand3")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_brand")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_xs")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_medium")};
`;

const ContentColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  min-width: 0;
  padding-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const ItemContent = styled.div<{ showBorder?: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_standard")};
  border: ${({ theme, showBorder }) =>
      showBorder ? theme.resolve("border_md") : "0"}
    solid ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  padding: ${({ theme, showBorder }) =>
    showBorder ? theme.resolve("spacing_md") : "0"};
`;

/**
 * A vertical line component used to connect timeline items visually.
 * Now uses flex layout inside AvatarColumn to fill remaining space.
 */
export const VersionTimelineLine = styled.div`
  width: 1px;
  flex: 1;
  background-color: ${({ theme }) => theme.resolve("color_border_light")};
`;

export const LogTimeline = ({
  id,
  username,
  date,
  message,
  isExpanded = true,
  children,
  onToggle,
  showToggleButton = true,
  showBorder = true,
  buttonText,
}: {
  id: string;
  username: string;
  date: string;
  message: React.ReactNode;
  isExpanded?: boolean;
  children?: React.ReactNode;
  onToggle?: () => void;
  showToggleButton?: boolean;
  showBorder?: boolean;
  buttonText?: string;
}) => {
  const formattedDate = formatDateTimeWithTime(date);

  return (
    <ItemContainer id={id}>
      <AvatarColumn>
        <AvatarWrapper>
          <Avatar>{getInitials(username)}</Avatar>
        </AvatarWrapper>
        <TimelineLineContainer>
          <VersionTimelineLine />
        </TimelineLineContainer>
      </AvatarColumn>
      <ContentColumn>
        <LogHeader
          username={username}
          date={formattedDate}
          message={message}
          isExpanded={isExpanded}
          onToggle={onToggle}
          showToggleButton={showToggleButton}
          buttonText={buttonText}
        />
        {isExpanded && children && (
          <ItemContent showBorder={showBorder}>{children}</ItemContent>
        )}
      </ContentColumn>
    </ItemContainer>
  );
};
